import { Link, Navigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Trophy } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SiteHeader } from '@/components/SiteHeader';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { useMySubmissions } from '@/hooks/useMySubmissions';
import { formatDate } from '@/lib/utils';

export function MySubmissionsPage() {
	const { user } = useAuth();
	const { data: submissions, isLoading } = useMySubmissions();
	const qc = useQueryClient();

	const compIds = submissions ? Array.from(new Set(submissions.map((s) => s.CompetitionId))) : [];
	const { data: competitions } = useQuery({
		queryKey: ['competition-lookup', compIds],
		enabled: compIds.length > 0,
		queryFn: async () => {
			const { data, error } = await supabase
				.from('Competitions')
				.select('Id, Slug, Name, Status, SubmissionCloseAt')
				.in('Id', compIds);
			if (error) throw error;
			return data as Array<{
				Id: string;
				Slug: string;
				Name: string;
				Status: string;
				SubmissionCloseAt: string;
			}>;
		},
	});

	const withdraw = useMutation({
		mutationFn: async (submissionId: string) => {
			const { error } = await supabase
				.from('Submissions')
				.update({ Status: 'withdrawn', WithdrawnAt: new Date().toISOString() })
				.eq('Id', submissionId);
			if (error) throw error;
		},
		onSuccess: () => {
			toast.success('Withdrawn. The slot has been used.');
			qc.invalidateQueries({ queryKey: ['my-submissions'] });
		},
		onError: (e) => toast.error((e as Error).message),
	});

	if (!user) return <Navigate to="/login" replace />;

	const compById = new Map(competitions?.map((c) => [c.Id, c]) ?? []);

	return (
		<>
			<SiteHeader />
			<main className="mx-auto max-w-4xl space-y-6 px-5 py-10">
				<header className="flex items-center gap-3">
					<Trophy className="h-7 w-7 text-loomlite-purple-soft" />
					<h1 className="m-0 font-heading text-3xl font-bold">My submissions</h1>
				</header>

				{isLoading && <p className="text-loomlite-muted">Loading…</p>}
				{submissions && submissions.length === 0 && (
					<p className="text-loomlite-muted">You haven't submitted to any competitions yet.</p>
				)}

				<div className="space-y-3">
					{submissions?.map((s) => {
						const comp = compById.get(s.CompetitionId);
						const canWithdraw =
							s.Status === 'submitted' &&
							comp?.Status === 'open' &&
							new Date(comp.SubmissionCloseAt) > new Date();
						return (
							<Card key={s.Id}>
								<CardHeader>
									<div className="flex items-start justify-between gap-3">
										<div>
											<CardTitle>{s.Title}</CardTitle>
											<p className="mt-1 text-xs text-loomlite-dim">
												{s.EntityType} · submitted {formatDate(s.SubmittedAt)}
											</p>
										</div>
										<Badge
											variant={
												s.Status === 'submitted'
													? 'success'
													: s.Status === 'withdrawn'
														? 'muted'
														: 'default'
											}
										>
											{s.Status}
										</Badge>
									</div>
								</CardHeader>
								<CardContent className="flex items-center justify-between">
									<Button variant="outline" size="sm" asChild>
										<Link to={comp ? `/competitions/${comp.Slug}` : '/'}>
											{comp?.Name ?? 'View competition'}
										</Link>
									</Button>
									{canWithdraw && (
										<Button
											variant="destructive"
											size="sm"
											disabled={withdraw.isPending}
											onClick={() => {
												if (
													window.confirm(
														'Withdraw this entry?\n\nThis will permanently use one of your submission slots.',
													)
												)
													withdraw.mutate(s.Id);
											}}
										>
											Withdraw
										</Button>
									)}
								</CardContent>
							</Card>
						);
					})}
				</div>
			</main>
		</>
	);
}
